import { ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent } from "@mcleod/components";
import { Api, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutMapBrokerageCode } from "./autogen/AutogenLayoutMapBrokerageCode";

const TYPE_STATE_TO_EXTERNAL_CODE = "S2E";
const TYPE_SCAC_TO_EXTERNAL_CODE = "S2S";
const TYPE_PACKAGING_TO_EXTERNAL_CODE = "PKG";
const TYPE_GLA_CODE_TO_EXTERNAL_CODE = "G2E";
const SOURCE_PROJECT_44 = "P44";
const SOURCE_SMC3 = "SMC3";

export class MapBrokerageCode extends AutogenLayoutMapBrokerageCode {

    private _codeType: string;
    private _lastToText: string = "";

    public get codeType(): string { return this._codeType; }
    public set codeType(codeType: string) { this._codeType = codeType; }

    override onLoad() {
        this.panelFromErrorMessage.visible = false;
        this.panelToErrorMessage.visible = false;
    }

    sourceMapBrokerageCodeBeforeExecution(event: DataSourceExecutionEvent): void {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.applyUrlFiltersToSearch(event);
        }
    }

    applyUrlFiltersToSearch(event: DataSourceExecutionEvent) {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (!urlProps) return;
        const { codeType, fromCode } = urlProps;
        if (!StringUtil.isEmptyString(codeType)) {
            event.filter.code_type = codeType;
        }
        if (!StringUtil.isEmptyString(fromCode)) {
            event.filter.from_code = fromCode;
        }
    }

    manageWarningPanels() {
        this.panelFromErrorMessage.visible = !StringUtil.isEmptyString(this.mainDataSource.activeRow.get("from_error_message"));
        this.panelToErrorMessage.visible = !StringUtil.isEmptyString(this.mainDataSource.activeRow.get("to_error_message"));
    }

    setupPageByType(type: string) {
        this.codeType = type;
        switch (type) {
            case "GLA": {
                this.textboxToCode.lookupModel = "lme/dispatch/gl-assignment"
                this.textboxToCode.lookupModelLayout = "lme/dispatch/GLAssignmentLookup";
                this.textboxToCode.lookupModelDisplayField = "description";
                this.textboxToCode.lookupModelResultField = "gla_code";
                this.textboxFromCode.caption = "External Code";
                this.textboxToCode.caption = "GLA Code";
                break;
            }
            case "OTH": {
                this.textboxToCode.lookupModel = "lme/dispatch/charge-code-lookup"
                this.textboxToCode.lookupModelLayout = "lme/dispatch/OtherChargeLookup";
                this.textboxToCode.lookupModelDisplayField = "descr";
                this.textboxToCode.lookupModelResultField = "id";

                this.textboxFromCode.lookupModel = "lme/dispatch/gl-assignment"
                this.textboxFromCode.lookupModelLayout = "lme/dispatch/GLAssignmentLookup";
                this.textboxFromCode.lookupModelDisplayField = "description";
                this.textboxFromCode.lookupModelResultField = "gla_code";

                this.textboxFromCode.caption = "GLA Code";
                this.textboxToCode.caption = "Other Charge Code";
                break;
            }
            case "CHG": {
                this.textboxToCode.lookupModel = "lme/settlement/deduct-code-lookup"
                this.textboxToCode.lookupModelLayout = "lme/general/DeductCodeLookup";
                this.textboxToCode.lookupModelDisplayField = "short_desc";
                this.textboxToCode.lookupModelResultField = "id";
                this.textboxToCode.lookupModelMinChars = 1;
                this.textboxToCode.lookupModelLayoutWidth = 500;
                this.textboxToCode.required = true;

                this.textboxFromCode.lookupModel = "lme/dispatch/gl-assignment"
                this.textboxFromCode.lookupModelLayout = "lme/dispatch/GLAssignmentLookup";
                this.textboxFromCode.lookupModelDisplayField = "description";
                this.textboxFromCode.lookupModelResultField = "gla_code";

                this.textboxFromCode.caption = "GLA Code";
                this.textboxToCode.caption = "Deduction/Earning Code";
                break;
            }
            case "G2E": {
                this.textboxFromCode.lookupModel = "lme/dispatch/gl-assignment"
                this.textboxFromCode.lookupModelLayout = "lme/dispatch/GLAssignmentLookup";
                this.textboxFromCode.lookupModelDisplayField = "description";
                this.textboxFromCode.lookupModelResultField = "gla_code";
                this.textboxFromCode.caption = "GLA Code";
                this.textboxToCode.caption = "External Charge Code";
                this.checkboxSaveAsBlank.visible = true;
                break;
            }
            case TYPE_PACKAGING_TO_EXTERNAL_CODE: {
                this.textboxFromCode.lookupModel = "lme/dispatch/packaging-type"
                this.textboxFromCode.lookupModelLayout = "lme/general/PackagingTypeLookup";
                this.textboxFromCode.lookupModelDisplayField = "field_code_desc";
                this.textboxFromCode.lookupModelResultField = "field_code";
                this.textboxFromCode.lookupModelMinChars = 1;
                this.textboxFromCode.lookupModelAllowShowAllResults = true;
                this.textboxFromCode.lookupModelLayoutWidth = 400;
                this.textboxFromCode.caption = "Packaging Type";
                this.textboxToCode.caption = "External Code";
                this.textboxToCode.required = true;
                break;
            }
            case TYPE_STATE_TO_EXTERNAL_CODE: {
                this.textboxFromCode.lookupModel = "common/states";
                this.textboxFromCode.lookupModelLayout = "lme/dispatch/brltl/stateLookup";
                this.textboxFromCode.lookupModelDisplayField = "name";
                this.textboxFromCode.lookupModelResultField = "id";
                this.textboxFromCode.lookupModelMinChars = 1;
                this.textboxFromCode.lookupModelAllowShowAllResults = true;
                this.textboxFromCode.lookupModelLayoutWidth = 300;
                this.textboxFromCode.caption = "State";
                this.textboxToCode.caption = "External";
                this.textboxToCode.required = true;
                break;
            }
            case TYPE_SCAC_TO_EXTERNAL_CODE: {
                this.textboxFromCode.lookupModel = null;
                this.textboxFromCode.lookupModelLayout = null;
                this.textboxFromCode.caption = "P44 SCAC";
                this.textboxToCode.caption = "Internal SCAC";
                this.textboxToCode.required = true;
            }
        }
    }

    /** This is an event handler for the afterModeChange event of sourceMapBrokerageCode. */
    async sourceMapBrokerageCodeAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode !== DataSourceMode.ADD) return;

        if (!StringUtil.isEmptyString(this.codeType)) {
            this.mainDataSource.activeRow?.set("code_type", this.codeType);
            this.textboxCodeType.setSelectedItemFromValue(this.codeType);
            this.textboxCodeSource.focus();
            this.textboxCodeType.enabled = false;
        }

        if (this.codeType === TYPE_PACKAGING_TO_EXTERNAL_CODE) {
            this.textboxCodeSource.removeItemsWithValue("MCLD");
            return;
        }

        if (this.codeType === TYPE_SCAC_TO_EXTERNAL_CODE) {
            this.mainDataSource.activeRow?.set("code_source", "P44");
            this.textboxCodeSource.setSelectedItemFromValue("P44");
            this.textboxFromCode.focus();
            this.textboxCodeSource.enabled = false;
            return;
        }

        if (this.codeType === TYPE_STATE_TO_EXTERNAL_CODE) {
            const contractRecordsResp = await Api.search("lme/dispatch/contract-management-records");
            const data = contractRecordsResp?.data[0];
            const isP44Active = data?.p44_active ?? false;
            const isSMC3Active = data?.smc3_active ?? false;
            if (isSMC3Active && !isP44Active) {
                this.mainDataSource.activeRow?.set("code_source", SOURCE_SMC3);
                this.textboxCodeSource.setSelectedItemFromValue(SOURCE_SMC3);
                this.textboxCodeSource.enabled = false;
                this.textboxFromCode.focus();
            } else if (isP44Active && !isSMC3Active) {
                this.mainDataSource.activeRow?.set("code_source", SOURCE_PROJECT_44);
                this.textboxCodeSource.setSelectedItemFromValue(SOURCE_PROJECT_44);
                this.textboxCodeSource.enabled = false;
                this.textboxFromCode.focus();
            } else {
                this.textboxCodeSource.focus();
            }
        }

    }

    sourceMapBrokerageCodeAfterExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource.mode = DataSourceMode.UPDATE) {
            const codeType = event.dataSource.activeRow.get("code_type");
            this.setupPageByType(codeType);
            const hasToCodeLookupData = event.dataSource.activeRow?.hasLookupModelData("to_code") === true;
            this.textboxToCode.lookupModelAllowFreeform = !hasToCodeLookupData;
            if (TYPE_GLA_CODE_TO_EXTERNAL_CODE === codeType) {
                this._lastToText = this.textboxToCode.text;
                this.manageChecked(this.sourceMapBrokerageCode.activeRow.get("to_code") == null);
            }
            event.dataSource.displayDataInBoundComponents();
        }
    }

    checkboxSaveAsBlankOnChange(event: ClickEvent) {
        this.manageChecked(this.checkboxSaveAsBlank.checked);
    }

    manageChecked(isChecked: boolean) {
        if (isChecked) {
            this._lastToText = this.textboxToCode.text;
            this.sourceMapBrokerageCode.activeRow.set("to_code", "");
            this.sourceMapBrokerageCode.displayDataInBoundComponents();
            this.textboxToCode.enabled = false;
            this.checkboxSaveAsBlank.checked = isChecked;
        } else {
            this.sourceMapBrokerageCode.activeRow.set("to_code", this._lastToText);
            this.sourceMapBrokerageCode.displayDataInBoundComponents();
            this.textboxToCode.enabled = true;
        }
    }

}
